import { useState } from "react";
import { ActionModal, FIELD_LABEL_CLASS, INPUT_CLASS } from "./ActionModal";
import type { HistoryLog } from "../../types";

/**
 * Remove one row from a history a human reads.
 *
 * The copy is doing real work here. "Remove" reads as "undo", and it is not:
 * the row is hidden from these pages and every machine derivation still sees
 * it, so removing a silence does not end it, removing a lock event does not
 * lift the lock, and removing a state change does not restore the state before
 * it. Saying so once, at the moment of the act, is cheaper than explaining
 * afterwards why the hotel is still silenced.
 *
 * The reason is mandatory — a hidden row leaves only that sentence behind.
 */
export function RemoveHistoryRowModal({
  log,
  label,
  detail,
  onSubmit,
  onClose,
  pending,
}: {
  log: HistoryLog;
  /** What the row is, in the reader's words — "🔕 Silenced", "Unhealthy →
   *  Monitoring", "fix logged". */
  label: string;
  /** The row's own sentence, so nobody removes the wrong one. */
  detail?: string | null;
  onSubmit: (reason: string) => void;
  onClose: () => void;
  pending?: boolean;
}) {
  const [reason, setReason] = useState("");

  return (
    <ActionModal
      title="Remove from history"
      submitLabel="Remove"
      danger
      onSubmit={() => onSubmit(reason.trim())}
      onClose={onClose}
      pending={pending}
      disabled={!reason.trim()}
    >
      <div className="px-3 py-2.5 rounded-md bg-[#F4F4F6] border border-[#E9E9EB]">
        <div className="text-sm font-medium text-content-primary">{label}</div>
        {detail && (
          <div className="text-xs text-content-secondary mt-1">{detail}</div>
        )}
        <div className="text-[11px] text-gray-400 mt-1.5">{log}</div>
      </div>
      <div>
        <label className={FIELD_LABEL_CLASS}>Why is this being removed?</label>
        <input
          className={INPUT_CLASS}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. logged against the wrong hotel"
          autoFocus
        />
      </div>
      <p className="text-xs text-gray-400">
        This hides the row from the pages people read — it does not undo what
        the row records. A removed silence is still in force, a removed lock is
        still applied, a removed state change still happened. Stamped with who
        removed it and why.
      </p>
    </ActionModal>
  );
}
